import React, { useState, useRef, useEffect } from 'react';

/**
 * LegalDocumentModal - A modal component for displaying legal documents
 * that requires scrolling to the bottom before allowing acceptance.
 *
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Called when modal is closed without accepting
 * - onAccept: function - Called when user accepts the document
 * - title: string - Document title (e.g., "Terms of Use")
 * - children: ReactNode - The document content to display
 * - acceptButtonText: string - Custom text for accept button (default: "I Accept")
 */
const LegalDocumentModal = ({
  isOpen,
  onClose,
  onAccept,
  title,
  children,
  acceptButtonText = "I Accept"
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const contentRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      setIsChecked(false);
    }
  }, [isOpen]);

  // Check if user has scrolled to the bottom
  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Consider "scrolled to bottom" when within 20px of the bottom
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      if (isAtBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
      }
    }
  };

  // Check on initial render if content is short enough to not need scrolling
  useEffect(() => {
    if (isOpen && contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      // If content fits without scrolling, enable the checkbox immediately
      if (scrollHeight <= clientHeight + 20) {
        setHasScrolledToBottom(true);
      }
    }
  }, [isOpen, children]);

  const handleAccept = () => {
    if (isChecked) {
      onAccept();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div
            ref={contentRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto px-6 py-4 text-gray-700"
            style={{ maxHeight: 'calc(90vh - 200px)' }}
          >
            {children}
          </div>

          {/* Scroll Indicator */}
          {!hasScrolledToBottom && (
            <div className="px-6 py-2 bg-blue-50 border-t border-blue-100">
              <div className="flex items-center text-sm text-blue-700">
                <svg className="w-4 h-4 mr-2 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                Please scroll to the bottom to continue
              </div>
            </div>
          )}

          {/* Footer with Checkbox and Button */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Checkbox */}
              <label className={`flex items-start cursor-pointer ${!hasScrolledToBottom ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  disabled={!hasScrolledToBottom}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the {title}
                </span>
              </label>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAccept}
                  disabled={!isChecked}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition ${
                    isChecked
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {acceptButtonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentModal;
